// apps/provider/src/services/api/forms.js
// Form definitions, versions, publishing, responses, generated reports,
// attachments/downloads, public forms, patient forms, form-to-patient mapping,
// and form redirect validation.
//
// Methods: getFormDefinitions, getFormDefinitionsLite, getFormDefinitionsWithSettings, getFormDefinition, createFormDefinition,
//          updateFormDefinition, deleteFormDefinition,
//          getFormVersions, getFormVersion, createFormVersion,
//          updateFormDefinitionWithPublishing, generatePublishedUrl,
//          getExistingFormSlugs, validateSlug,
//          assignFormToLocation,
//          getFormResponses, getFormResponse, getFormResponseBySession,
//          updateFormResponse,
//          getFormGeneratedReports, getFormGeneratedReportById,
//          updateReportDownloadStatus,
//          getAttachmentDownloadUrl, downloadAttachment,
//          getPublishedForm, submitFormResponse, submitFormResponseWithSession,
//          generateFormSession, uploadFormAttachment,
//          testFormDataUpload,
//          getPatientForms, getPatientReports,
//          createFormPatientMapping, getFormPatientMapping,
//          updateFormPatientMapping, processFormToPatient,
//          validateFormRedirect, getQuestionTypeCompatibility
//
// Response types: @prism-ehr/shared → FormDefinition, FormVersion, FormResponse,
//                 FormGeneratedReport, FormPatientMapping, FormAttachmentUploadResult,
//                 FormSessionResult, FormRedirectValidation

import { isDemoEnvironment } from '@/config/platforms';
import { ensureFilenameExtension } from '@/utils/mimeUtils';

/**
 * @returns {Object} Forms domain methods (bound to `this` of the composed apiService)
 */
export function createFormsMethods() {
  return {

    // ============ Form Definitions (CRUD) ============

    /**
     * Get all form definitions for an organization, with optional location and type filters.
     *
     * Xano: GET /forms
     * Xano file: 610_forms_GET.xs
     *
     * @param {string} organizationId
     * @param {string|null} [locationId=null] - Filter by location
     * @param {string|null} [type=null] - Filter by form type (scheduling, intake, followup, pathway)
     * @returns {Promise<import('@prism-ehr/shared').FormDefinition[]>}
     */
    async getFormDefinitions(organizationId, locationId = null, type = null) {
      let endpoint = `/forms?organization_id=${organizationId}`;
      if (locationId) endpoint += `&location_id=${locationId}`;
      if (type) endpoint += `&type=${type}`;

      return this.request(endpoint, { method: 'GET' });
    },

    /**
     * Lite form list for pickers: id, name, type, status, location_id, organization_id.
     * No form_version definition and no settings. Same filters as getFormDefinitions.
     *
     * Xano: GET /forms-lite
     * Xano file: forms-lite_GET.xs
     *
     * @param {string} organizationId
     * @param {string|null} [locationId=null]
     * @param {string|null} [type=null]
     * @returns {Promise<Pick<import('@prism-ehr/shared').FormDefinition, 'id'|'name'|'type'|'status'|'location_id'|'organization_id'>[]>}
     */
    async getFormDefinitionsLite(organizationId, locationId = null, type = null) {
      let endpoint = `/forms-lite?organization_id=${organizationId}`;
      if (locationId) endpoint += `&location_id=${locationId}`;
      if (type) endpoint += `&type=${type}`;

      return this.request(endpoint, { method: 'GET' });
    },

    /**
     * Form list with settings but without the form_version definition.
     * For readers of form.settings (provider assignment, feedback config).
     *
     * Xano: GET /forms-settings
     * Xano file: forms-settings_GET.xs
     *
     * @param {string} organizationId
     * @param {string|null} [locationId=null]
     * @param {string|null} [type=null]
     * @returns {Promise<Omit<import('@prism-ehr/shared').FormDefinition, 'form_version'>[]>}
     */
    async getFormDefinitionsWithSettings(organizationId, locationId = null, type = null) {
      let endpoint = `/forms-settings?organization_id=${organizationId}`;
      if (locationId) endpoint += `&location_id=${locationId}`;
      if (type) endpoint += `&type=${type}`;

      return this.request(endpoint, { method: 'GET' });
    },

    /**
     * Get a single form definition by ID.
     *
     * Xano: GET /forms/{formId}
     * Xano file: 609_forms_forms_id_GET.xs
     *
     * @param {string} formId
     * @returns {Promise<import('@prism-ehr/shared').FormDefinition>}
     */
    async getFormDefinition(formId) {
      return this.request(`/forms/${formId}`, { method: 'GET' });
    },

    /**
     * Create a new form definition.
     *
     * Xano: POST /forms
     * Xano file: 611_forms_POST.xs
     *
     * @param {Object} formData - Form definition data
     * @returns {Promise<import('@prism-ehr/shared').FormDefinition>}
     */
    async createFormDefinition(formData) {
      return this.request('/forms', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
    },

    /**
     * Update an existing form definition.
     *
     * Xano: PATCH /forms/{formId}
     * Xano file: 612_forms_forms_id_PATCH.xs
     *
     * @param {string} formId
     * @param {Object} formData - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').FormDefinition>}
     */
    async updateFormDefinition(formId, formData) {
      return this.request(`/forms/${formId}`, {
        method: 'PATCH',
        body: JSON.stringify(formData)
      });
    },

    /**
     * Delete a form definition.
     *
     * Xano: DELETE /forms/{formId}
     * Xano file: 608_forms_forms_id_DELETE.xs
     *
     * @param {string} formId
     * @returns {Promise<void>}
     */
    async deleteFormDefinition(formId) {
      return this.request(`/forms/${formId}`, { method: 'DELETE' });
    },

    // ============ Form Versions ============

    /**
     * Get all versions for a form.
     *
     * Xano: GET /forms-versions
     * Xano file: 615_form_versions_GET.xs
     *
     * @param {string} formId
     * @returns {Promise<import('@prism-ehr/shared').FormVersion[]>}
     */
    async getFormVersions(formId) {
      return this.request(`/forms-versions?form_id=${formId}`, { method: 'GET' });
    },

    /**
     * Get a specific form version by ID.
     *
     * Xano: GET /form-versions/{versionId}
     * Xano file: 614_form_versions_form_versions_id_GET.xs
     *
     * @param {string} versionId
     * @returns {Promise<import('@prism-ehr/shared').FormVersion>}
     */
    async getFormVersion(versionId) {
      return this.request(`/form-versions/${versionId}`, { method: 'GET' });
    },

    /**
     * Create a new form version.
     *
     * Xano: POST /form-versions
     * Xano file: 616_form_versions_POST.xs
     *
     * @param {Object} versionData - Version data including form_id and definition
     * @returns {Promise<import('@prism-ehr/shared').FormVersion>}
     */
    async createFormVersion(versionData) {
      return this.request('/form-versions', {
        method: 'POST',
        body: JSON.stringify(versionData)
      });
    },

    // ============ Form Publishing ============

    /**
     * Update a form definition with optional publishing.
     * Validates slug uniqueness if publishing with a custom slug,
     * then generates the published URL and updates the form.
     *
     * Composite: calls validateSlug → generatePublishedUrl → updateFormDefinition
     *
     * @param {string} formId
     * @param {Object} formData - Form definition fields to update
     * @param {boolean} [publish=false] - Whether to publish the form
     * @param {string|null} [customSlug=null] - Custom URL slug for publishing
     * @returns {Promise<import('@prism-ehr/shared').FormDefinition>}
     */
    async updateFormDefinitionWithPublishing(formId, formData, publish = false, customSlug = null, settings = null) {
      if (publish && customSlug) {
        const isValidSlug = await this.validateSlug(customSlug);
        if (!isValidSlug) {
          throw new Error('Invalid or duplicate slug');
        }
      }

      let updatedFormData = { ...formData };

      if (publish) {
        const publishedUrl = await this.generatePublishedUrl(formId, customSlug, settings);
        updatedFormData = {
          ...updatedFormData,
          status: 'published',
          published_url: publishedUrl
        };

        if (customSlug) {
          updatedFormData.slug = customSlug;
        }
      }

      return this.updateFormDefinition(formId, updatedFormData);
    },

    /**
     * Generate a published URL for a form, using the form ID or a custom slug.
     * Uses demo domain in demo environments, production domain otherwise.
     *
     * Utility (no API call)
     *
     * @param {string} formId
     * @param {string|null} [customSlug=null]
     * @returns {Promise<string>} The full published form URL
     */
    async generatePublishedUrl(formId, customSlug = null, settings = null) {
      let formBaseDomain;
      if (isDemoEnvironment()) {
        formBaseDomain = 'https://demo-form.prismehr.com/';
      } else {
        formBaseDomain = 'https://form.prismehr.com/';
      }
      const urlPath = customSlug || formId;
      const suffix = settings?.form?.deliveryMode === 'ai_assistant' ? '/assistant' : '';
      return `${formBaseDomain}${urlPath}${suffix}`;
    },

    /**
     * Get all existing form URL slugs for uniqueness validation.
     *
     * Xano: GET /form-url-slugs
     * Xano file: 727_form_url_slugs_GET.xs
     *
     * @returns {Promise<string[]>}
     */
    async getExistingFormSlugs() {
      try {
        return await this.request('/form-url-slugs', { method: 'GET' });
      } catch (error) {
        console.error('[API] Failed to fetch existing slugs:', error);
        return [];
      }
    },

    /**
     * Validate a slug: checks format (lowercase alphanumeric with hyphens)
     * and uniqueness against existing slugs.
     *
     * Composite: calls getExistingFormSlugs
     *
     * @param {string} slug
     * @returns {Promise<boolean>} True if slug is valid and available
     */
    async validateSlug(slug) {
      if (!slug) return false;

      const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
      if (!slugRegex.test(slug)) {
        return false;
      }

      const existingSlugs = await this.getExistingFormSlugs();
      return !existingSlugs.includes(slug);
    },

    // ============ Form Assignment ============

    /**
     * Assign a form to a location with a specific type.
     *
     * Xano: POST /form-assignments
     * Xano file: Not found in staging
     *
     * @param {string} locationId
     * @param {string} formId
     * @param {string} formType - Type of assignment (e.g., 'intake', 'scheduling')
     * @returns {Promise<Object>}
     */
    async assignFormToLocation(locationId, formId, formType) {
      return this.request('/form-assignments', {
        method: 'POST',
        body: JSON.stringify({
          form_id: formId,
          type: formType,
          location_id: locationId
        })
      });
    },

    // ============ Form Responses ============

    /**
     * Get paginated form responses with optional filters.
     *
     * Xano: GET /form-responses-with-pagination
     * Xano file: 728_form_responses_with_pagination_GET.xs
     *
     * @param {string|null} [formId=null] - Filter by form
     * @param {string|null} [organizationId=null] - Filter by organization
     * @param {string|null} [locationId=null] - Filter by location
     * @param {string|null} [startDate=null] - Filter start date
     * @param {string|null} [endDate=null] - Filter end date
     * @param {number} [page=1] - Page number
     * @param {number} [perPage=25] - Items per page
     * @returns {Promise<{items: Array, cursorNext: number|null, itemsReceived: number}>}
     */
    async getFormResponses(formId = null, organizationId = null, locationId = null, startDate = null, endDate = null, page = 1, perPage = 25) {
      let endpoint = `/form-responses-with-pagination?page=${page}&per_page=${perPage}`;
      const params = [];

      if (formId) params.push(`form_id=${formId}`);
      if (organizationId) params.push(`organization_id=${organizationId}`);
      if (locationId) params.push(`location_id=${locationId}`);
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);

      if (params.length > 0) {
        endpoint += `&${params.join('&')}`;
      }

      return this.request(endpoint, { method: 'GET' });
    },

    /**
     * Get a single form response by ID.
     *
     * Xano: GET /form-responses/{responseId}
     * Xano file: 619_form_responses_form_responses_id_GET.xs
     *
     * @param {string} responseId
     * @returns {Promise<import('@prism-ehr/shared').FormResponse>}
     */
    async getFormResponse(responseId) {
      return this.request(`/form-responses/${responseId}`, { method: 'GET' });
    },

    /**
     * Get a form response by session token (public/unauthenticated).
     * Used for multi-page forms where the patient resumes via session token.
     *
     * Xano: GET /public/form-responses
     * Xano file: 720_public_form_responses_GET.xs
     *
     * @param {string} sessionToken
     * @returns {Promise<import('@prism-ehr/shared').FormResponse|null>}
     */
    async getFormResponseBySession(sessionToken) {
      try {
        return await this.publicRequest(`/public/form-responses?session_token=${sessionToken}`);
      } catch (error) {
        console.error('[API] Failed to fetch form response by session:', error);
        return null;
      }
    },

    /**
     * Update a form response.
     *
     * Xano: PATCH /form-responses/{responseId}
     * Xano file: 622_form_responses_form_responses_id_PATCH.xs
     *
     * @param {string} responseId
     * @param {Object} responseData - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').FormResponse>}
     */
    async updateFormResponse(responseId, responseData) {
      return this.request(`/form-responses/${responseId}`, {
        method: 'PATCH',
        body: JSON.stringify(responseData)
      });
    },

    /**
     * Reassign the therapist attributed to a form response.
     * Cascades the new provider onto any generated reports for the response.
     * Requires Admin or the forms_reassign_provider permission.
     *
     * Xano: PATCH /form-responses-assign-provider
     * Xano file: form_responses_assign_provider_PATCH.xs
     *
     * @param {string} responseId
     * @param {string} providerId
     * @returns {Promise<import('@prism-ehr/shared').FormResponse>}
     */
    async assignFormResponseProvider(responseId, providerId) {
      return this.request('/form-responses-assign-provider', {
        method: 'PATCH',
        body: JSON.stringify({ form_response_id: responseId, provider_id: providerId })
      });
    },

    // ============ Form Generated Reports ============

    /**
     * Get paginated form-generated reports for an organization.
     *
     * Xano: GET /form-generated-reports-with-pagination
     * Xano file: 729_form_generated_reports_with_pagination_GET.xs
     *
     * @param {string} organizationId
     * @param {string|null} [locationId=null]
     * @param {number} [page=1]
     * @param {number} [perPage=25]
     * @returns {Promise<{items: import('@prism-ehr/shared').FormGeneratedReport[], cursorNext: number|null, itemsReceived: number}>}
     */
    async getFormGeneratedReports(organizationId, locationId = null, page = 1, perPage = 25) {
      let endpoint = `/form-generated-reports-with-pagination?organization_id=${organizationId}&page=${page}&per_page=${perPage}`;
      if (locationId) {
        endpoint += `&location_id=${locationId}`;
      }

      return this.request(endpoint, { method: 'GET' });
    },

    /**
     * Get a single form-generated report by ID.
     *
     * Xano: GET /form-generated-reports/{id}
     * Xano file: 629_form_generated_reports_form_generated_reports_id_GET.xs
     *
     * @param {string} id
     * @returns {Promise<import('@prism-ehr/shared').FormGeneratedReport>}
     */
    async getFormGeneratedReportById(id) {
      return this.request(`/form-generated-reports/${id}`, { method: 'GET' });
    },

    /**
     * Mark a report as downloaded (updates downloaded flag to true).
     *
     * Xano: PATCH /form-generated-reports/{reportId}
     * Xano file: 632_form_generated_reports_form_generated_reports_id_PATCH.xs
     *
     * @param {string} reportId
     * @param {Object} report - Current report data (merged with downloaded: true)
     * @returns {Promise<import('@prism-ehr/shared').FormGeneratedReport>}
     */
    async updateReportDownloadStatus(reportId, report) {
      return this.request(`/form-generated-reports/${reportId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...report,
          downloaded: true
        })
      });
    },

    async markReportAsRead(reportId, userId, existingReadBy = []) {
      if ((existingReadBy || []).some(entry => entry.user_id === userId)) return;
      return this.request(`/form-generated-reports/${reportId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          read_by: [...(existingReadBy || []), { user_id: userId, read_at: Date.now() }]
        })
      });
    },

    // ============ Attachments & Downloads ============

    /**
     * Generate a signed URL for downloading a file attachment.
     * In demo environments, returns the path directly (already public).
     * In production, requests a signed URL with a 30-minute TTL.
     *
     * Xano: GET /attachment/download-url
     * Xano file: 462_attachment_download_url_GET.xs
     *
     * @param {string} path - File path or URL
     * @returns {Promise<string>} Signed download URL or direct path
     */
    async getAttachmentDownloadUrl(path) {
      if (isDemoEnvironment()) {
        return path;
      }

      try {
        const response = await this.request(`/attachment/download-url?path=${path}&ttl=1800`);
        return response;
      } catch (error) {
        console.error('[API] Failed to generate download URL:', error);
        throw new Error('Failed to generate download URL');
      }
    },

    /**
     * Fetch an attachment as a blob with the correct MIME type.
     *
     * @param {Object} attachment
     * @param {string} [attachment.path] - File path
     * @param {string} [attachment.url] - Direct URL
     * @param {string} [attachment.file_url] - Alternative URL field
     * @param {string} [attachment.mime] - MIME type from Xano file object
     * @param {string} [attachment.type] - Alternative MIME field
     * @param {string} [mimeTypeHint] - Explicit MIME override
     * @returns {Promise<Blob>}
     */
    async fetchAttachmentBlob(attachment, mimeTypeHint) {
      const attachmentPath = attachment.path || attachment.url || attachment.file_url;
      if (!attachmentPath) {
        throw new Error('No path available for attachment');
      }

      let downloadUrl;
      if (isDemoEnvironment() && (attachment.url || attachment.file_url)) {
        downloadUrl = attachment.url || attachment.file_url;
      } else {
        downloadUrl = await this.getAttachmentDownloadUrl(attachmentPath);
      }

      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error('Fetch failed');

      const rawBlob = await response.blob();
      const effectiveMime = mimeTypeHint || attachment.mime || attachment.type;

      if (effectiveMime && effectiveMime !== 'application/octet-stream' && effectiveMime !== rawBlob.type) {
        return new Blob([rawBlob], { type: effectiveMime });
      }
      return rawBlob;
    },

    /**
     * Download a file attachment to the user's device.
     * Fetches the file (via signed URL or direct URL), creates a blob
     * with the correct MIME type, and triggers a browser download.
     *
     * @param {Object} attachment
     * @param {string} [attachment.path] - File path
     * @param {string} [attachment.url] - Direct URL
     * @param {string} [attachment.file_url] - Alternative URL field
     * @param {string} [attachment.name] - Filename for download
     * @param {string} [attachment.filename] - Alternative filename field
     * @param {string} [attachment.mime] - MIME type from Xano file object
     * @param {string} [attachment.type] - Alternative MIME field
     * @param {Object} [options]
     * @param {string} [options.mimeType] - Explicit MIME type hint
     * @returns {Promise<void>}
     */
    async downloadAttachment(attachment, options = {}) {
      try {
        const blob = await this.fetchAttachmentBlob(attachment, options.mimeType);
        const effectiveMime = blob.type !== 'application/octet-stream' ? blob.type : null;
        const rawName = attachment.name || attachment.filename || 'download';
        const fileName = ensureFilenameExtension(rawName, effectiveMime);

        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error('[API] Failed to download attachment:', error);
        throw error;
      }
    },

    // ============ Public Form Methods ============

    /**
     * Get a published form by ID or slug (public/unauthenticated).
     * Fetches the form definition, validates it's published, then fetches
     * the current version and merges the definition.
     *
     * Xano: GET /public/forms/{idOrSlug} + GET /public/form-versions/{versionId}
     * Xano files: 722_public_forms_forms_id_GET.xs + 721_public_form_versions_form_versions_id_GET.xs
     *
     * @param {string} idOrSlug - Form ID or custom slug
     * @returns {Promise<import('@prism-ehr/shared').FormDefinition|null>}
     */
    async getPublishedForm(idOrSlug) {
      try {
        let form = null;

        try {
          form = await this.publicRequest(`/public/forms/${idOrSlug}`, { method: 'GET' });
        } catch (error) {
          // Silent fail, form might not exist
        }

        if (!form || form.status !== 'published') {
          return null;
        }

        if (!form.current_version_id) {
          return null;
        }

        const version = await this.publicRequest(`/public/form-versions/${form.current_version_id}`, { method: 'GET' });

        return {
          ...form,
          ...version.definition
        };
      } catch (error) {
        console.error('[API] Failed to fetch published form:', error);
        return null;
      }
    },

    /**
     * Submit a completed form response (public/unauthenticated).
     *
     * Xano: POST /public/form-responses-prism
     * Xano file: 907_public_form_responses_prism_POST.xs
     *
     * @param {Object} responseData - Form response data
     * @returns {Promise<import('@prism-ehr/shared').FormResponse>}
     */
    async submitFormResponse(responseData) {
      return this.publicRequest('/public/form-responses-prism', {
        method: 'POST',
        body: JSON.stringify({
          ...responseData,
          created_at: Date.now(),
          status: responseData.status || 'complete'
        })
      });
    },

    /**
     * Submit a form response with optional session token generation (public/unauthenticated).
     * Used for multi-page forms where the patient can return later.
     *
     * Xano: POST /public/form-responses-prism
     * Xano file: 907_public_form_responses_prism_POST.xs
     *
     * @param {Object} responseData - Form response data
     * @param {boolean} [generateSession=false] - Whether to generate a session token
     * @returns {Promise<import('@prism-ehr/shared').FormResponse>}
     */
    async submitFormResponseWithSession(responseData, generateSession = false) {
      const payload = {
        ...responseData,
        created_at: Date.now(),
        status: responseData.status || 'complete'
      };

      if (generateSession) {
        payload.generate_session = true;
      }

      try {
        const result = await this.publicRequest('/public/form-responses-prism', {
          method: 'POST',
          body: JSON.stringify(payload)
        });

        return result;
      } catch (error) {
        console.error('[API] Failed to submit form response with session:', error);
        throw error;
      }
    },

    /**
     * Generate a new form session token (public/unauthenticated).
     * Used to resume multi-page form submissions.
     *
     * Xano: POST /public/generate-session
     * Xano file: 723_public_generate_session_POST.xs
     *
     * @returns {Promise<import('@prism-ehr/shared').FormSessionResult>}
     */
    async generateFormSession() {
      return this.publicRequest('/public/generate-session', {
        method: 'POST',
        body: JSON.stringify({
          timestamp: Date.now()
        })
      });
    },

    /**
     * Upload a file attachment for a form question (public/unauthenticated).
     * Converts the file to base64 before sending.
     *
     * Xano: POST /public/upload-attachment
     * Xano file: 725_public_upload_attachment_POST.xs
     *
     * @param {File} file - The file to upload
     * @param {string} questionId - The question this attachment belongs to
     * @param {string} sessionToken - Session token for the form submission
     * @param {Function|null} [onProgress=null] - Progress callback (unused, for future use)
     * @returns {Promise<import('@prism-ehr/shared').FormAttachmentUploadResult>}
     */
    async uploadFormAttachment(file, questionId, sessionToken, onProgress = null) {
      const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = error => reject(error);
        });
      };

      const base64Data = await fileToBase64(file);

      return this.publicRequest('/public/upload-attachment', {
        method: 'POST',
        timeoutMs: 120000,
        body: JSON.stringify({
          file_data: base64Data,
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
          question_id: questionId,
          session_token: sessionToken
        })
      });
    },

    // ============ AI Form Assistant ============

    /**
     * Send a message to the AI form assistant (public/unauthenticated).
     * Passes form template + conversation history to the AI assistant for conversational
     * form field extraction via tool use.
     *
     * Xano: POST /public/ai-form-assistant
     * Xano file: 1173_public_ai_form_assistant_POST.xs
     *
     * @param {Object} payload
     * @param {string} payload.form_id - Form ID
     * @param {string} payload.form_name - Form display name
     * @param {Array} payload.form_template - Simplified questions array
     * @param {Array} [payload.sections] - Section metadata
     * @param {Array} payload.messages - Conversation format [{role, content}]
     * @param {Object} payload.extracted_data - Current {questionId: value} state
     * @returns {Promise<{message: string, extractions: Array<{question_id: string, value: any, answer_text: string}>}>}
     */
    async sendAssistantMessage(payload) {
      return this.publicRequest('/public/ai-form-assistant', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
    },

    /**
     * Generate text-to-speech audio via OpenAI TTS (public/unauthenticated).
     *
     * Xano: POST /public/ai-tts
     * Xano file: 1140_public_ai_tts_POST.xs
     *
     * @param {string} text - Text to speak
     * @param {string} [voice='nova'] - OpenAI voice (alloy, echo, fable, onyx, nova, shimmer)
     * @returns {Promise<{audio: string, format: string}>} Base64-encoded audio
     */
    async generateTTS(text, voice = 'nova') {
      return this.publicRequest('/public/ai-tts', {
        method: 'POST',
        timeoutMs: 120000,
        body: JSON.stringify({ text, voice })
      });
    },

    // ============ Test/Debug ============

    /**
     * Test FormData upload endpoint (dev/debug only).
     *
     * Xano: POST /test-upload
     * Xano file: 456_attachment_test_POST.xs
     *
     * @param {File} file
     * @returns {Promise<Object>}
     */
    async testFormDataUpload(file) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('test_field', 'test_value');

      return this.request('/test-upload', {
        method: 'POST',
        timeoutMs: 120000,
        body: formData
      });
    },

    // ============ Patient Forms & Reports ============

    /**
     * Get form responses for a specific patient.
     *
     * Xano: GET /form-responses
     * Xano file: 620_form_responses_GET.xs
     *
     * @param {string} patientId - Patient identifier
     * @returns {Promise<import('@prism-ehr/shared').FormResponse[]>}
     */
    async getPatientForms(patientId) {
      return this.request(`/form-responses?patient_identifier=${patientId}`, {
        method: 'GET'
      });
    },

    /**
     * Get generated reports for a specific patient.
     *
     * Xano: GET /form-generated-reports
     * Xano file: 630_form_generated_reports_GET.xs
     *
     * @param {string} patientId
     * @returns {Promise<import('@prism-ehr/shared').FormGeneratedReport[]>}
     */
    async getPatientReports(patientId) {
      return this.request(`/form-generated-reports?patient_id=${patientId}`, {
        method: 'GET'
      });
    },

    // ============ Form-to-Patient Mapping ============

    /**
     * Create a form-to-patient field mapping configuration.
     * Maps form question answers to patient record fields.
     *
     * Xano: POST /form_patient_mapping
     * Xano file: 758_form_patient_mapping_POST.xs
     *
     * @param {string} formId
     * @param {Object} mappings - Field mapping configuration
     * @returns {Promise<import('@prism-ehr/shared').FormPatientMapping>}
     */
    async createFormPatientMapping(formId, mappings) {
      return this.request('/form_patient_mapping', {
        method: 'POST',
        body: JSON.stringify({
          form_id: formId,
          mappings: mappings,
          created_at: Date.now()
        })
      });
    },

    /**
     * Get the form-to-patient mapping configuration for a form.
     *
     * Xano: GET /form_patient_mapping
     * Xano file: 757_form_patient_mapping_GET.xs
     *
     * @param {string} formId
     * @returns {Promise<import('@prism-ehr/shared').FormPatientMapping>}
     */
    async getFormPatientMapping(formId) {
      return this.request(`/form_patient_mapping?form_id=${formId}`, {
        method: 'GET'
      });
    },

    /**
     * Update a form-to-patient mapping configuration.
     *
     * Xano: PATCH /form_patient_mapping/{id}
     * Xano file: 759_form_patient_mapping_form_patient_mapping_id_PATCH.xs
     *
     * @param {string} id - Mapping record ID
     * @param {Object} mappings - Updated mapping configuration
     * @returns {Promise<import('@prism-ehr/shared').FormPatientMapping>}
     */
    async updateFormPatientMapping(id, mappings) {
      return this.request(`/form_patient_mapping/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          mappings: mappings,
          updated_at: Date.now()
        })
      });
    },

    /**
     * Process a form submission to create or update a patient record.
     * Uses the form-to-patient mapping to extract patient data from form answers.
     *
     * Xano: POST /process_form_to_patient
     * Xano file: Not found in staging
     *
     * @param {string} formResponseId
     * @returns {Promise<Object>}
     */
    async processFormToPatient(formResponseId) {
      return this.request('/process_form_to_patient', {
        method: 'POST',
        body: JSON.stringify({
          form_response_id: formResponseId,
          processed_at: Date.now()
        })
      });
    },

    // ============ Form Redirect Validation ============

    /**
     * Validate a form redirect configuration.
     * Checks for self-redirects and circular redirects (A→B→A).
     *
     * Composite: calls getFormDefinition
     *
     * @param {string} sourceFormId
     * @param {string} targetFormId
     * @param {string} organizationId
     * @returns {Promise<import('@prism-ehr/shared').FormRedirectValidation>}
     */
    async validateFormRedirect(sourceFormId, targetFormId, organizationId) {
      if (sourceFormId === targetFormId) {
        return {
          isValid: false,
          error: 'Forms cannot redirect to themselves'
        };
      }

      try {
        const targetForm = await this.getFormDefinition(targetFormId);
        const formSettings = targetForm?.settings?.form;

        if (formSettings?.redirectType === 'form' &&
          formSettings?.formRedirect?.targetFormId === sourceFormId) {
          return {
            isValid: false,
            error: 'Circular redirect detected - target form redirects back to this form'
          };
        }

        return { isValid: true };
      } catch (error) {
        console.error('[API] Error validating form redirect:', error);
        return {
          isValid: false,
          error: 'Unable to validate redirect configuration'
        };
      }
    },

    /**
     * Check compatibility between two question types for form field mapping.
     * Returns whether a source question type can be mapped to a target question type.
     *
     * Utility (no API call, pure function)
     *
     * @param {string} sourceType - Source question type
     * @param {string} targetType - Target question type
     * @returns {boolean}
     */
    getQuestionTypeCompatibility(sourceType, targetType) {
      const compatibilityMap = {
        'text': ['text', 'textarea', 'email', 'phone'],
        'textarea': ['text', 'textarea'],
        'email': ['text', 'textarea', 'email'],
        'phone': ['text', 'textarea', 'phone'],
        'number': ['number', 'text', 'textarea'],
        'date': ['date', 'dateTime', 'text', 'textarea'],
        'dateTime': ['date', 'dateTime', 'text', 'textarea'],
        'multipleChoice': ['multipleChoice', 'select', 'text', 'textarea'],
        'checkbox': ['checkbox', 'multipleChoice', 'select'],
        'select': ['select', 'multipleChoice', 'text', 'textarea'],
        'signature': ['signature'],
        'fileUpload': ['fileUpload'],
        'bodyDiagram': ['bodyDiagram'],
        'address': ['address', 'textarea'],
        'entryAndDropdown': ['entryAndDropdown', 'text', 'textarea'],
        'doubleEntry': ['doubleEntry', 'text', 'textarea'],
        'displayText': [],
        'imageAndText': []
      };

      const compatibleTypes = compatibilityMap[sourceType] || [];
      return compatibleTypes.includes(targetType);
    },

    // ============ Form Sending ============

    /**
     * Send a published form to a recipient via email and/or SMS.
     *
     * Xano: POST /form-send
     * Xano file: form-send_POST.xs
     *
     * @param {Object} data
     * @param {string} data.form_id - Form definition ID (must be published)
     * @param {string} data.recipient_name - Recipient display name
     * @param {string} [data.recipient_email] - Required if delivery_channel is 'email' or 'both'
     * @param {string} [data.recipient_phone] - Required if delivery_channel is 'sms' or 'both'
     * @param {string} data.delivery_channel - 'email', 'sms', or 'both'
     * @param {string} [data.message] - Optional custom message from the provider
     * @returns {Promise<import('@prism-ehr/shared').FormSendResult>}
     */
    async sendForm(data) {
      return this.request('/form-send', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Resend a previously sent form link, reusing the same send token so partial progress is preserved.
     *
     * Xano: POST /form-send-log-resend
     * Xano file: form-send-log-resend_POST.xs
     *
     * @param {number} sendLogId - form_send_log record ID
     * @returns {Promise<import('@prism-ehr/shared').FormSendResult>}
     */
    async resendFormSend(sendLogId) {
      return this.request('/form-send-log-resend', {
        method: 'POST',
        body: JSON.stringify({ send_log_id: sendLogId })
      });
    },

    async getFormSendHistory(organizationId, { page = 1, perPage = 25, patientId, formId, source, query } = {}) {
      let endpoint = `/form-send-log?organization_id=${organizationId}&page=${page}&per_page=${perPage}`;
      if (patientId) endpoint += `&patient_id=${patientId}`;
      if (formId) endpoint += `&form_id=${formId}`;
      if (source) endpoint += `&source=${source}`;
      if (query) endpoint += `&query=${encodeURIComponent(query)}`;
      return this.request(endpoint, { method: 'GET' });
    },

    /**
     * Pending (non-terminal) form sends for a patient in the last 90 days.
     *
     * @param {string} patientId
     * @param {string} organizationId
     * @param {Object} [opts]
     * @param {number} [opts.caseId] - Only sends scoped to this case
     * @param {boolean} [opts.unscopedOnly] - Only sends with no case
     */
    async getPatientPendingForms(patientId, organizationId, { caseId, unscopedOnly } = {}) {
      let endpoint = `/form-send-log-patient?patient_id=${patientId}&organization_id=${organizationId}`;
      if (caseId != null && caseId !== '') endpoint += `&case_id=${encodeURIComponent(caseId)}`;
      if (unscopedOnly) endpoint += '&unscoped_only=true';
      return this.request(endpoint, { method: 'GET' });
    },

    /**
     * Resolve a send token to get prefill data and partial progress.
     * @param {string} sendToken
     * @returns {Promise<import('@prism-ehr/shared').FormPrefillResult>}
     */
    async getFormPrefill(sendToken) {
      return this.publicRequest(`/public/form-prefill?send_token=${encodeURIComponent(sendToken)}`, { method: 'GET' });
    },

    /**
     * Save form progress after a section is completed.
     * @param {import('@prism-ehr/shared').FormProgressSaveRequest} data
     * @returns {Promise<import('@prism-ehr/shared').FormProgressSaveResult>}
     */
    async saveFormProgress(data) {
      return this.publicRequest('/public/form-progress', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },
  };
}
